import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IFreightDetailEntityAttributes extends IModelAttributes {
	quantity: number;
	description: string;
	width: number;
	height: number;
	length: number;
	weight: number;
	cubic: number;

	freightCategoryId?: string;
	freightCategory?: Models.FreightCategoryEntity | Models.IFreightCategoryEntityAttributes;
	freightTypeId?: string;
	freightType?: Models.FreightTypeEntity | Models.IFreightTypeEntityAttributes;
	freightHandlings: Array<Models.FreightDetailFreightHandling | Models.IFreightDetailFreightHandlingAttributes>;
	freightNotifyContactId?: string;
	freightNotifyContact?: Models.FreightContactEntity | Models.IFreightContactEntityAttributes;
	freightReceiverContactId?: string;
	freightReceiverContact?: Models.FreightContactEntity | Models.IFreightContactEntityAttributes;
	tickets: Array<Models.TicketEntity | Models.ITicketEntityAttributes>;
}
@entity('FreightDetailEntity', 'Freight Detail')
export default class FreightDetailEntity extends Model implements IFreightDetailEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The quantity of the freight item.
	 */
	@observable
	@attribute<FreightDetailEntity, number>()
	public quantity: number;

	/**
	 * A free text field for the customer to enter a description about their freight.
	 */
	@observable
	@attribute<FreightDetailEntity, string>()
	public description: string;

	/**
	 * The width of the freight.
	 */
	@observable
	@attribute<FreightDetailEntity, number>()
	public width: number;

	/**
	 * The height of the freight.
	 */
	@observable
	@attribute<FreightDetailEntity, number>()
	public height: number;

	/**
	 * The length of the freight.
	 */
	@observable
	@attribute<FreightDetailEntity, number>()
	public length: number;

	/**
	 * The weight of the freight (per item).
	 */
	@observable
	@attribute<FreightDetailEntity, number>()
	public weight: number;

	/**
	 * The cubic volume of the freight (per item).
	 */
	@observable
	@attribute<FreightDetailEntity, number>()
	public cubic: number;

	@observable
	@attribute()
	public freightCategoryId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public freightCategory: Models.FreightCategoryEntity;

	@observable
	@attribute()
	public freightTypeId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public freightType: Models.FreightTypeEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public freightHandlings: Models.FreightDetailFreightHandling[] = [];

	@observable
	@attribute()
	public freightNotifyContactId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public freightNotifyContact: Models.FreightContactEntity;

	@observable
	@attribute()
	public freightReceiverContactId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public freightReceiverContact: Models.FreightContactEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public tickets: Models.TicketEntity[] = [];

	constructor(attributes?: Partial<IFreightDetailEntityAttributes>) {
		super(attributes);

		this.freightHandlings = [];
		this.tickets = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IFreightDetailEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.quantity !== undefined) {
				this.quantity = attributes.quantity;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.width !== undefined) {
				this.width = attributes.width;
			}
			if (attributes.height !== undefined) {
				this.height = attributes.height;
			}
			if (attributes.length !== undefined) {
				this.length = attributes.length;
			}
			if (attributes.weight !== undefined) {
				this.weight = attributes.weight;
			}
			if (attributes.cubic !== undefined) {
				this.cubic = attributes.cubic;
			}
			if (attributes.freightCategoryId !== undefined) {
				this.freightCategoryId = attributes.freightCategoryId;
			}
			if (attributes.freightCategory !== undefined) {
				if (attributes.freightCategory === null) {
					this.freightCategory = attributes.freightCategory;
				} else if (attributes.freightCategory instanceof Models.FreightCategoryEntity) {
					this.freightCategory = attributes.freightCategory;
					this.freightCategoryId = attributes.freightCategory.id;
				} else {
					this.freightCategory = new Models.FreightCategoryEntity(attributes.freightCategory);
					this.freightCategoryId = this.freightCategory.id;
				}
			}
			if (attributes.freightTypeId !== undefined) {
				this.freightTypeId = attributes.freightTypeId;
			}
			if (attributes.freightType !== undefined) {
				if (attributes.freightType === null) {
					this.freightType = attributes.freightType;
				} else if (attributes.freightType instanceof Models.FreightTypeEntity) {
					this.freightType = attributes.freightType;
					this.freightTypeId = attributes.freightType.id;
				} else {
					this.freightType = new Models.FreightTypeEntity(attributes.freightType);
					this.freightTypeId = this.freightType.id;
				}
			}
			if (attributes.freightHandlings !== undefined && Array.isArray(attributes.freightHandlings)) {
				if (!this.freightHandlings) {
					this.freightHandlings = [];
				}
				for (const model of attributes.freightHandlings) {
					if (model instanceof Models.FreightDetailFreightHandling) {
						this.freightHandlings.push(model);
					} else {
						this.freightHandlings.push(new Models.FreightDetailFreightHandling(model));
					}
				}
			}
			if (attributes.freightNotifyContactId !== undefined) {
				this.freightNotifyContactId = attributes.freightNotifyContactId;
			}
			if (attributes.freightNotifyContact !== undefined) {
				if (attributes.freightNotifyContact === null) {
					this.freightNotifyContact = attributes.freightNotifyContact;
				} else if (attributes.freightNotifyContact instanceof Models.FreightContactEntity) {
					this.freightNotifyContact = attributes.freightNotifyContact;
					this.freightNotifyContactId = attributes.freightNotifyContact.id;
				} else {
					this.freightNotifyContact = new Models.FreightContactEntity(attributes.freightNotifyContact);
					this.freightNotifyContactId = this.freightNotifyContact.id;
				}
			}
			if (attributes.freightReceiverContactId !== undefined) {
				this.freightReceiverContactId = attributes.freightReceiverContactId;
			}
			if (attributes.freightReceiverContact !== undefined) {
				if (attributes.freightReceiverContact === null) {
					this.freightReceiverContact = attributes.freightReceiverContact;
				} else if (attributes.freightReceiverContact instanceof Models.FreightContactEntity) {
					this.freightReceiverContact = attributes.freightReceiverContact;
					this.freightReceiverContactId = attributes.freightReceiverContact.id;
				} else {
					this.freightReceiverContact = new Models.FreightContactEntity(attributes.freightReceiverContact);
					this.freightReceiverContactId = this.freightReceiverContact.id;
				}
			}
			if (attributes.tickets !== undefined && Array.isArray(attributes.tickets)) {
				if (!this.tickets) {
					this.tickets = [];
				}
				for (const model of attributes.tickets) {
					if (model instanceof Models.TicketEntity) {
						this.tickets.push(model);
					} else {
						this.tickets.push(new Models.TicketEntity(model));
					}
				}
			}
		}
	}
}
